import { useState } from 'react'
import { setAiDetectConsent } from '../utils/aiDetectionApi'
import type { AiDetectApiResponse } from '../utils/aiDetectionApi'

interface AiDetectionBannerProps {
  /** Whether to show the consent prompt (user hasn't opted in yet) */
  showConsent: boolean
  /** Called when user clicks Enable */
  onConsent: () => void
  /** Whether analysis is in progress */
  analyzing: boolean
  /** Analysis result (null if not yet analyzed) */
  result: AiDetectApiResponse | null
}

export function AiDetectionBanner({ showConsent, onConsent, analyzing, result }: AiDetectionBannerProps) {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  // Consent prompt
  if (showConsent) {
    return (
      <div style={{
        margin: '16px 0',
        padding: '16px',
        borderRadius: '8px',
        backgroundColor: '#f0f4ff',
        border: '1px solid #c7d2fe',
      }}>
        <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#3730a3' }}>
          Additional Verification Available
        </p>
        <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#4338ca' }}>
          Want to run additional AI manipulation analysis on this file?
          This sends the image to our server for analysis.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => {
              setAiDetectConsent(true)
              onConsent()
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#4f46e5',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Enable AI Detection
          </button>
          <button
            onClick={() => setDismissed(true)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #c7d2fe',
              backgroundColor: 'transparent',
              color: '#4338ca',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Not now
          </button>
        </div>
      </div>
    )
  }

  // Loading state
  if (analyzing) {
    return (
      <div style={{
        margin: '16px 0',
        padding: '16px',
        borderRadius: '8px',
        backgroundColor: '#f0f4ff',
        border: '1px solid #c7d2fe',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '3px solid #c7d2fe',
          borderTopColor: '#4f46e5',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <span style={{ color: '#4338ca', fontSize: '14px' }}>Analyzing image for AI generation...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  // Result: AI detected — only show warning at >= 20% confidence
  if (result?.aiDetected === true && (result?.confidence ?? 0) >= 0.20) {
    const confidence = result.confidence != null ? Math.round(result.confidence * 100) : null
    return (
      <div style={{
        margin: '16px 0',
        padding: '16px',
        borderRadius: '8px',
        backgroundColor: '#faf5ff',
        border: '2px solid #a78bfa',
      }}>
        <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#6d28d9', fontSize: '16px' }}>
          thephenom.ai has detected AI manipulation
          {confidence != null && ` (${confidence}% confidence)`}
        </p>
        <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#5b21b6', lineHeight: 1.5 }}>
          This image shows signs of AI generation or manipulation.
        </p>
        <p style={{ margin: 0, fontSize: '13px', color: '#7c3aed' }}>
          Verified by Verifieddit.com • {new Date(result.analysisDate).toLocaleString()}
          {result.cached && ' • Cached result'}
        </p>
      </div>
    )
  }

  // Result: Not AI
  if (result && result.aiDetected === false) {
    return (
      <div style={{
        margin: '16px 0',
        padding: '12px 16px',
        borderRadius: '8px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #86efac',
      }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
          No AI generation detected in this image.
          {result.confidence != null && ` (${Math.round((1 - result.confidence) * 100)}% confidence)`}
        </p>
      </div>
    )
  }

  // Result: Error / unavailable
  if (result?.error) {
    const message = result.error === 'rate_limit'
      ? 'AI detection rate limit reached. Try again later.'
      : result.error === 'quota_exceeded'
      ? 'AI detection monthly quota exceeded. Contact verifieddit.com to upgrade.'
      : 'AI analysis temporarily unavailable.'
    return (
      <div style={{
        margin: '16px 0',
        padding: '12px 16px',
        borderRadius: '8px',
        backgroundColor: '#fffbeb',
        border: '1px solid #fcd34d',
      }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>{message}</p>
      </div>
    )
  }

  return null
}
